import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Typography } from '@mui/material';
import { sessionStorage } from 'app/services/storage';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { LOGIN_METHOD, PASSKEY_ENROLLMENT_PROMPT_FLAG } from 'constants/auth';
import useForm from 'hooks/useForm';
import { getPasswordField } from 'utils/validation';

import { submitSetInitialPasswordAction } from 'store/auth/actions';

import { LoginTestIds } from '../../Login.testIds';

import { PASSWORD_INPUT_PROPS } from './SetInitialPasswordTab.constants';

const confirmMatchTest = function check(value) {
  return value === this.parent.newPassword;
};

const setPasswordSchema = object().shape({
  newPassword: getPasswordField(),
  confirmPassword: getPasswordField().test('match', 'Passwords do not match', confirmMatchTest),
});

const InputLabelProps = { shrink: true };

/**
 * DEV-4157: Mandatory set-password screen for TPO invitees who received a constrained activation
 * token (access-only, `accounts.activation` JWT claim). This tab is shown instead of completing
 * sign-in; the user must set a password before the app shell renders.
 *
 * The activation token is held in memory (never written to jwtManager / localStorage). On submit,
 * the action calls POST /api/user/set-initial-password with the activation token as bearer and
 * receives a full SecurityToken → completeSignIn.
 *
 * For PasskeyWithUsername companies, arms the one-time passkey enrollment prompt (DEV-3881) by
 * setting the sessionStorage flag BEFORE completing sign-in, exactly as Login.onFallbackToPassword
 * and Authentication.seedAutoLoginTokenIfHandoff do.
 *
 * @param {string} activationToken - The constrained access-only activation token (in memory only).
 * @param {string|null} signInMethod - The company's LOGIN_METHOD value, used to arm passkey enroll.
 */
const SetInitialPasswordTab = ({ activationToken, signInMethod }) => {
  const dispatch = useDispatch();
  const login = useSelector(({ auth }) => auth.login);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { form, isFormChanged, validationErrors, setSchema, handleChange, validateForm } = useForm({
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    setSchema(setPasswordSchema);
  }, [setSchema]);

  const onPasswordChange = useCallback(
    async e => {
      setSubmitError(null);
      await handleChange(e);
    },
    [handleChange],
  );

  const onConfirmPasswordChange = useCallback(
    async e => {
      setSubmitError(null);
      await handleChange(e, { newPassword: form.newPassword });
    },
    [form.newPassword, handleChange],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        // DEV-4157 + DEV-3881: For PasskeyWithUsername companies, arm the one-time enroll prompt
        // BEFORE completing sign-in so PasskeyEnrollmentHost sees the flag on the authenticated
        // transition (it consumes it on masterRole change, not on mount — DEV-4083 pattern).
        if (signInMethod === LOGIN_METHOD.PASSKEY_WITH_USERNAME) {
          sessionStorage.setItem(PASSKEY_ENROLLMENT_PROMPT_FLAG, true);
        }

        await dispatch(
          submitSetInitialPasswordAction({ newPassword: form.newPassword, activationToken }),
        );
      } catch (err) {
        // If the dispatch throws, clear the passkey flag — the user will retry and re-arm it then.
        if (signInMethod === LOGIN_METHOD.PASSKEY_WITH_USERNAME) {
          sessionStorage.removeItem(PASSKEY_ENROLLMENT_PROMPT_FLAG);
        }

        const message =
          err?.response?.data?.error?.message ||
          err?.response?.data?.title ||
          'Failed to set password. Please try again.';

        setSubmitError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [activationToken, dispatch, form.newPassword, signInMethod, validateForm],
  );

  const isDisabled = isSubmitting || login?.loading;

  return (
    <div className="w-full">
      <Typography className="mb-16 text-center" color="text.secondary" variant="medium14">
        Welcome! Please set a password for your account to continue.
      </Typography>

      <form noValidate className="flex flex-col justify-center w-full" onSubmit={onSubmit}>
        <TextFieldCDS
          required
          withStaticHelper
          InputLabelProps={InputLabelProps}
          className="mb-16"
          dataTestId={LoginTestIds.SET_PASSWORD_NEW_INPUT}
          disabled={isDisabled}
          error={!!validationErrors.newPassword}
          helperText={validationErrors.newPassword}
          inputProps={PASSWORD_INPUT_PROPS}
          label="New Password"
          name="newPassword"
          type="password"
          value={form.newPassword}
          variant="outlined"
          onChange={onPasswordChange}
        />

        <TextFieldCDS
          required
          withStaticHelper
          InputLabelProps={InputLabelProps}
          className="mb-16"
          dataTestId={LoginTestIds.SET_PASSWORD_CONFIRM_INPUT}
          disabled={isDisabled}
          error={!!validationErrors.confirmPassword}
          helperText={validationErrors.confirmPassword}
          inputProps={PASSWORD_INPUT_PROPS}
          label="Confirm Password"
          name="confirmPassword"
          type="password"
          value={form.confirmPassword}
          variant="outlined"
          onChange={onConfirmPasswordChange}
        />

        {submitError && (
          <div className="flex flex-col items-center text-red">
            <Typography className="py-8" variant="medium14">
              {submitError}
            </Typography>
          </div>
        )}

        <Button
          className="w-full mx-auto mt-16 normal-case"
          color="primary"
          data-testid={LoginTestIds.SET_PASSWORD_SUBMIT_BUTTON}
          disabled={!isFormChanged || isDisabled}
          type="submit"
          variant="contained"
        >
          Set Password & Sign In
        </Button>
      </form>
    </div>
  );
};

SetInitialPasswordTab.propTypes = {
  activationToken: PropTypes.string.isRequired,
  signInMethod: PropTypes.string,
};

export default SetInitialPasswordTab;
